import { OPTIONS_PER_CONTRACT } from 'utils/shared/quantlib/utils';
import { calculateSingleLegMetrics } from 'utils/shared/quantlib/singleLegCalculator';
import { ENABLE_OPTION_FLOW_READ_MODE } from 'utils/shared/trades/optionFlowRead';
import { getTradePositionSign } from 'utils/shared/trades/flowDirection';
import { hasSolvedGreeks } from 'utils/shared/trades/hasSolvedGreeks';
import {
  getOptionFlowRowSentiment,
  getOptionFlowSideCode,
  getOptionFlowStrategyPosition,
  getOptionFlowStrategyPositionLabel,
  OptionFlowStrategyPosition,
} from 'utils/shared/trades/getOptionFlowRowSentiment';
import { getOptionFlowReadLabel } from 'utils/shared/trades/getOptionFlowSideIntelligence';
import { getSectorShortName } from 'utils/shared/trades/sectors';
import type { OptionFlowEnrichmentFields, Trade } from 'utils/shared/trades/types';

export type OptionFlowCsvValue = string | number;
export type OptionFlowCsvRow = Record<string, OptionFlowCsvValue>;

type OptionFlowCsvTrade = Trade & OptionFlowEnrichmentFields;

const DEFAULT_VOLATILITY = 0.3;
const NUMBER_PRECISION = 8;

const toFiniteNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
};

const round = (value: number, precision = NUMBER_PRECISION): number => {
  const multiplier = 10 ** precision;
  return Math.round((value + Number.EPSILON) * multiplier) / multiplier;
};

const numberOrBlank = (value: unknown, precision = NUMBER_PRECISION): number | '' => {
  const number = toFiniteNumber(value);
  return number === undefined ? '' : round(number, precision);
};

const getTradeDate = (trade: OptionFlowCsvTrade): string => {
  const rawDate = String(trade.date || '').trim();
  const isoDate = rawDate.match(/^\d{4}-\d{2}-\d{2}/)?.[0];

  if (isoDate) {
    return isoDate;
  }

  const timestamp = new Date(rawDate || String(trade.createdAt || '')).getTime();
  return Number.isFinite(timestamp) ? new Date(timestamp).toISOString().slice(0, 10) : '';
};

const getExpiry = (trade: OptionFlowCsvTrade): string => {
  const year = toFiniteNumber(trade.expireYear);
  const month = toFiniteNumber(trade.expireMonth);
  const day = toFiniteNumber(trade.expireDay);

  if (year !== undefined && month !== undefined && day !== undefined) {
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  const parts = trade.expiryString?.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  return parts ? `${parts[1]}-${parts[2].padStart(2, '0')}-${parts[3].padStart(2, '0')}` : '';
};

const getSingleLegMetrics = (trade: OptionFlowCsvTrade) => {
  const strategyPosition = getOptionFlowStrategyPosition(trade);
  const spot = toFiniteNumber(trade.spot) ?? toFiniteNumber(trade.priceExternalApi);
  const strike = toFiniteNumber(trade.strike);
  const premium = toFiniteNumber(trade.price);
  const daysToExpiry = toFiniteNumber(trade.daysToExpiry);
  const yearsToExpiry = toFiniteNumber(trade.yearsToExpiry);
  const nbboMidIv = toFiniteNumber(trade.nbboMidIv);
  const impliedVol = toFiniteNumber(trade.impliedVol);
  const riskFreeRate = toFiniteNumber(trade.riskFreeRate);
  const cp = trade.cp?.toLowerCase();

  if (
    strategyPosition === OptionFlowStrategyPosition.Neutral ||
    spot === undefined ||
    spot <= 0 ||
    strike === undefined ||
    strike <= 0 ||
    premium === undefined ||
    premium < 0 ||
    (cp !== 'c' && cp !== 'call' && cp !== 'p' && cp !== 'put')
  ) {
    return undefined;
  }

  try {
    return calculateSingleLegMetrics({
      spot,
      strike,
      premium,
      type: cp === 'c' || cp === 'call' ? 'call' : 'put',
      numberOfContracts: strategyPosition === OptionFlowStrategyPosition.Long ? 1 : -1,
      // Prefer the model inputs captured with the quote. Older rows fall back to
      // their regular IV and the calculator's default risk-free rate.
      volatility:
        nbboMidIv !== undefined && nbboMidIv > 0
          ? nbboMidIv
          : impliedVol !== undefined && impliedVol > 0
          ? impliedVol
          : DEFAULT_VOLATILITY,
      riskFreeRate,
      // Evaluate historical rows as of their trade, not as of the download date.
      // A non-positive captured value means the feed carries no intraday remainder
      // (0DTE rows report 0), so fall through to the expiration date rather than
      // collapsing every same-day trade's probability into a hard 0% / 100%.
      timeToExpirationInYears:
        yearsToExpiry !== undefined && yearsToExpiry > 0
          ? yearsToExpiry
          : daysToExpiry !== undefined && daysToExpiry > 0
          ? daysToExpiry / 365.25
          : undefined,
      expirationDateString: getExpiry(trade) || undefined,
    });
  } catch {
    return undefined;
  }
};

const getDeltaAdjustedNotional = (trade: OptionFlowCsvTrade): number | '' => {
  const delta = toFiniteNumber(trade.delta);
  const size = toFiniteNumber(trade.size);
  const spot = toFiniteNumber(trade.spot) ?? toFiniteNumber(trade.priceExternalApi);

  if (delta === undefined || size === undefined || spot === undefined) {
    return '';
  }

  return round(delta * size * OPTIONS_PER_CONTRACT * spot * getTradePositionSign(trade), 2);
};

/**
 * Canonical option-flow CSV schema shared by realtime, historical, weekly, and
 * ticker trade exports. Every column describes the individual trade; ticker-level
 * calculations live in the separate aggregate export. Percent/rate/IV enrichment
 * fields remain decimal values to match the source payload.
 */
export const formatOptionFlowCsvData = (trades: OptionFlowCsvTrade[]): OptionFlowCsvRow[] =>
  trades.map((trade) => {
    const singleLegMetrics = getSingleLegMetrics(trade);
    const openInterest = toFiniteNumber(trade.openInterest);
    const size = toFiniteNumber(trade.size);
    // The feed writes 0 across implied vol and every greek when its model does not
    // solve. Those are missing values, not measurements, so nothing derived from
    // them is reported. See hasSolvedGreeks.
    const solved = hasSolvedGreeks(trade);

    return {
      'Trade Date': getTradeDate(trade),
      Time: [trade.time, trade.timeSubscript].filter(Boolean).join(' '),
      Ticker: trade.ticker || '',
      'C/P': trade.cp || '',
      'Trade Side': getOptionFlowSideCode(trade),
      ...(ENABLE_OPTION_FLOW_READ_MODE ? { Read: getOptionFlowReadLabel(trade) } : {}),
      Expiry: getExpiry(trade),
      'Days to Expiration': numberOrBlank(trade.daysToExpiry, 4),
      'Spot ($)': numberOrBlank(trade.spot ?? trade.priceExternalApi, 2),
      'Strike ($)': numberOrBlank(trade.strike, 2),
      Otm: (() => {
        const otmPercent = toFiniteNumber(trade.otmPercent);
        return otmPercent === undefined ? '' : round(otmPercent / 100, 4);
      })(),
      'Price ($)': numberOrBlank(trade.price, 2),
      'Trade Sentiment': getOptionFlowRowSentiment(trade),
      'Strategy Direction': getOptionFlowStrategyPositionLabel(trade),
      Size: numberOrBlank(trade.size, 2),
      'Open Interest': numberOrBlank(trade.openInterest, 2),
      'Implied Vol': solved ? numberOrBlank(trade.impliedVol, 8) : '',
      Type: trade.orderType || '',
      'Premium ($)': numberOrBlank(trade.premium, 2),
      Sector: getSectorShortName(trade.tickerDetails?.sector || ''),
      'Heat Score': Math.max(0, Math.min(100, Math.round(trade.scoreWidth ?? 0))),
      'Size/OI':
        openInterest === undefined
          ? ''
          : openInterest <= 0
          ? 'Open'
          : round((size || 0) / openInterest),
      // `Infinite` is a real value for an uncapped leg, not a missing one, so it stays a
      // word. Everything else is a number or a blank — never the string 'N/A', which
      // turns the whole column into text in a spreadsheet.
      'Max Profit':
        typeof singleLegMetrics?.maxProfit === 'number'
          ? round(singleLegMetrics.maxProfit, 2)
          : singleLegMetrics?.maxProfit ?? '',
      'Max Loss':
        typeof singleLegMetrics?.maxLoss === 'number'
          ? round(singleLegMetrics.maxLoss, 2)
          : singleLegMetrics?.maxLoss ?? '',
      // Decimal, matching `Probability of Touch`. One probability convention per file.
      'Probability of Profit': solved ? numberOrBlank(singleLegMetrics?.probabilityOfProfit) : '',
      'Breakeven Price':
        singleLegMetrics?.breakEvenPrice !== undefined
          ? round(singleLegMetrics.breakEvenPrice, 2)
          : '',
      Delta: solved ? numberOrBlank(trade.delta) : '',
      'Delta Adjusted Notional ($)': solved ? getDeltaAdjustedNotional(trade) : '',
      Gamma: solved ? numberOrBlank(trade.gamma) : '',
      Theta: solved ? numberOrBlank(trade.theta) : '',
      Vega: solved ? numberOrBlank(trade.vega) : '',
      Rho: solved ? numberOrBlank(trade.rho) : '',
      Vanna: numberOrBlank(trade.vanna),
      Vomma: numberOrBlank(trade.vomma),
      Charm: numberOrBlank(trade.charm),
      Zomma: numberOrBlank(trade.zomma),
      Color: numberOrBlank(trade.color),
      Speed: numberOrBlank(trade.speed),
      Veta: numberOrBlank(trade.veta),
      Vera: numberOrBlank(trade.vera),
      Ultima: numberOrBlank(trade.ultima),
      Lambda: numberOrBlank(trade.lambda),
      'Probability of Touch': numberOrBlank(trade.probTouch),
      'Risk-Free Rate': numberOrBlank(trade.riskFreeRate),
      'NBBO Mid IV': numberOrBlank(trade.nbboMidIv),
    };
  });
